"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlanSchema = exports.PlanFeatureSchema = exports.PlanFeatureLevelSchema = exports.PlanFeatureQuotaSchema = exports.PlanTotalPriceSchema = exports.PlanPriceSchema = exports.PlanType = exports.SubscriptionDiscountSchema = void 0;
const zod_1 = require("zod");
const __1 = require("..");
exports.SubscriptionDiscountSchema = zod_1.z.object({
    /**
     * Amount of the discount, in cents.
     */
    amount: zod_1.z.number(),
    /**
     * Period in which the discount should be applied based on the `subscribedAt` date of a Contract.
     * @see {@link IContract.subscribedAt}
     */
    period: zod_1.z.nativeEnum(__1.SubscriptionDiscountPeriod),
    /**
     * Number of cycles of a period in which the discount should be applied to, based on the `subscribedAt` date of a
     * Contract.
     * @example
     * // A R$ 100,00 discount with 3 months duration
     * const discount = {
     *   amount: 10000,
     *   period: SubscriptionDiscountPeriod.Months,
     *   periodDuration: 3,
     * };
     * @see {@link period}
     * @see {@link IContract.subscribedAt}
     */
    periodDuration: zod_1.z.number(),
    /**
     * Duration of the discount.
     */
    duration: zod_1.z.nativeEnum(__1.DiscountDuration).optional(),
    /**
     * witch recurrence the discount should be applied to.
     */
    recurrencesToApply: zod_1.z.array(zod_1.z.nativeEnum(__1.PlanRecurrence)),
    /**
     * Provider Discount ID
     */
    providerDiscountId: __1.ShortStringSchema.optional(),
    /**
     * Is a custom discount by default is false and the discount shoulb be the plan discount
     */
    isCustomDiscount: zod_1.z.boolean().optional(),
    /**
     * Discount ID is the same from the provider
     */
    id: __1.ShortStringSchema.optional(),
});
var PlanType;
(function (PlanType) {
    PlanType["Free"] = "FREE";
    PlanType["Paid"] = "PAID";
    PlanType["Alpha"] = "ALPHA";
})(PlanType || (exports.PlanType = PlanType = {}));
exports.PlanPriceSchema = __1.MoneySchema.extend({
    recurrence: zod_1.z.nativeEnum(__1.PlanRecurrence),
    providerPriceId: __1.ShortStringSchema.optional(),
    active: zod_1.z.boolean(),
    allowedPayments: zod_1.z.array(zod_1.z.nativeEnum(__1.PaymentMethod)).optional(),
});
exports.PlanTotalPriceSchema = exports.PlanPriceSchema.extend({
    /**
     * Discount percentage to be applied in the plan price.
     */
    discountPercentage: zod_1.z.number(),
    /**
     * Discount value to be applied in the plan price.
     */
    discountTotal: zod_1.z.number(),
    /**
     * Original plan price before any discount is applied.
     */
    planPriceAmount: zod_1.z.number(),
    /**
     * Provider Discount IDs
     */
    providerDiscountIds: zod_1.z.array(__1.ShortStringSchema).optional(),
});
/**
 * @description Feature quota schema for `quota_based` features.
 */
exports.PlanFeatureQuotaSchema = zod_1.z.object({
    id: __1.ShortStringSchema,
    name: __1.ShortStringSchema,
    description: __1.ShortStringSchema.optional(),
    type: zod_1.z.enum(['monthly', 'fixed']),
    unlimited: zod_1.z.boolean(),
    remaining: zod_1.z.number().optional(),
    used: zod_1.z.number().optional(),
    limit: zod_1.z.number(),
});
/**
 * @description Feature level schema for `level_based` features.
 */
exports.PlanFeatureLevelSchema = zod_1.z.object({
    id: __1.ShortStringSchema,
    name: __1.ShortStringSchema,
    description: __1.ShortStringSchema.optional(),
    level: zod_1.z.number(),
    limit: zod_1.z.number().optional(),
    additionalData: zod_1.z.object({}).passthrough().optional(),
});
/**
 * @description For more info: https://www.notion.so/Freemium-Rules-4d5afe314f4146a3b122aedc4712142f
 */
exports.PlanFeatureSchema = zod_1.z.object({
    id: zod_1.z.nativeEnum(__1.PlanFeatureId),
    name: __1.ShortStringSchema,
    description: __1.ShortStringSchema,
    available: zod_1.z.boolean(),
    type: zod_1.z.enum(['level_based', 'quota_based', 'toggle']),
    index: zod_1.z.number().min(1).optional(),
    levels: exports.PlanFeatureLevelSchema.array().optional(),
    maxAllowedLevel: exports.PlanFeatureLevelSchema.optional(),
    quota: exports.PlanFeatureQuotaSchema.optional(),
});
exports.PlanSchema = __1.BaseSchema.extend({
    name: __1.ShortStringSchema,
    description: __1.ShortStringSchema.optional(),
    /**
     * Effective price of a subscription with discount (if existing) applied to it.
     */
    prices: zod_1.z.array(exports.PlanPriceSchema),
    /**
     * Array of total prices, these are a combination of prices and discounts grouped by their recurrence.
     */
    totalPrices: zod_1.z.array(exports.PlanTotalPriceSchema),
    discounts: zod_1.z.array(exports.SubscriptionDiscountSchema).optional(),
    firstYearCommissionPercentage: zod_1.z.number().nonnegative().max(100).optional(),
    commissionPercentage: zod_1.z.number().nonnegative().max(100).optional(),
    trialDays: zod_1.z.number().nonnegative(),
    countryCode: zod_1.z.nativeEnum(__1.Alpha3Countries),
    active: zod_1.z.boolean(),
    position: zod_1.z.number().nonnegative(),
    /**
     * 0 - Alpha/Free;
     *
     * 1 - Starter;
     *
     * 2 - Plus/Pro;
     */
    level: zod_1.z.number().nonnegative(),
    bulletPoints: zod_1.z.array(__1.ShortStringSchema).optional(),
    highlight: zod_1.z.boolean().optional(),
    /**
     * @deprecated Use `providers` instead.
     */
    provider: __1.ShortStringSchema.optional(),
    providers: zod_1.z.nativeEnum(__1.PlanProvider).array().optional(),
    type: zod_1.z.nativeEnum(PlanType),
    /**
     * The features supported by the plan. For more info: https://www.notion.so/Freemium-Rules-4d5afe314f4146a3b122aedc4712142f
     */
    features: exports.PlanFeatureSchema.array(),
});
//# sourceMappingURL=plan.js.map